/**
 * Plan, usage and the upgrade path.
 *
 * The screen has one job that matters: tell a merchant where they stand
 * *before* their widget goes quiet. A customer who discovers their allowance
 * ran out because a shopper complained has been failed by this page, not by
 * the quota.
 */

import {
  useBilling,
  useOpenPortal,
  usePlans,
  useStartCheckout,
} from "../api/queries";
import type { BillingOverview, PlanSummary } from "../api/types";
import { useCurrentOrganization } from "../auth/session";
import {
  Badge,
  Button,
  Card,
  ErrorNote,
  Loading,
  Stat,
} from "../ui/primitives";

export function BillingPage() {
  const organization = useCurrentOrganization();
  const orgId = organization?.organization.id ?? "";

  const billing = useBilling(orgId);
  const plans = usePlans();
  const checkout = useStartCheckout(orgId);
  const portal = useOpenPortal(orgId);

  if (billing.isLoading) return <Loading />;
  if (billing.error) return <ErrorNote error={billing.error} />;
  if (!billing.data) return null;

  const data = billing.data;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-lg font-semibold">Plan and usage</h1>
        <p className="text-sm text-muted">
          {data.period_from_provider
            ? "Your billing period, as invoiced."
            : "Resets on the 1st of each month."}{" "}
          {new Date(data.period_start).toLocaleDateString()} –{" "}
          {new Date(data.period_end).toLocaleDateString()}
        </p>
      </div>

      {checkout.error != null && <ErrorNote error={checkout.error} />}
      {portal.error != null && <ErrorNote error={portal.error} />}

      <QuotaNotice data={data} />

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        <Stat
          label="Plan"
          value={data.plan.name}
          hint={
            data.plan.price_cents === 0
              ? "Free"
              : `$${(data.plan.price_cents / 100).toFixed(0)} / month`
          }
        />
        <Stat
          label="Conversations"
          value={
            data.conversations_limit < 0
              ? String(data.conversations_used)
              : `${data.conversations_used} / ${data.conversations_limit}`
          }
          hint={data.conversations_limit < 0 ? "Unlimited" : undefined}
          // Toned off this meter's own state, not the combined one: colouring
          // a healthy meter red because the *other* one ran out is how a
          // merchant ends up upgrading for the wrong reason.
          tone={meterTone(data.conversations_used, data.conversations_limit)}
        />
        <Stat
          label="Messages"
          value={
            data.messages_limit < 0
              ? String(data.messages_used)
              : `${data.messages_used} / ${data.messages_limit}`
          }
          hint={data.messages_limit < 0 ? "Unlimited" : "Every message is one AI answer"}
          tone={meterTone(data.messages_used, data.messages_limit)}
        />
      </div>

      {(data.conversations_limit >= 0 || data.messages_limit >= 0) && (
        <Card title="This period">
          <UsageBars data={data} />
        </Card>
      )}

      <Card
        title="Change plan"
        action={
          data.self_service_available && data.plan.price_cents > 0 ? (
            <Button size="sm" onClick={() => portal.mutate()} disabled={portal.isPending}>
              Manage subscription
            </Button>
          ) : undefined
        }
      >
        {!data.self_service_available && (
          <p className="mb-3 text-sm text-muted">
            Online payment is not enabled on this deployment. Contact us to
            change plan.
          </p>
        )}

        {plans.isLoading ? (
          <Loading />
        ) : (
          <ul className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {(plans.data ?? []).map((plan) => (
              <li key={plan.code}>
                <PlanCard
                  plan={plan}
                  current={plan.code === data.plan.code}
                  busy={checkout.isPending}
                  onChoose={() => checkout.mutate(plan.code)}
                />
              </li>
            ))}
          </ul>
        )}
      </Card>
    </div>
  );
}

/**
 * The warning, and the explanation.
 *
 * Three distinct states, because they need three different sentences: running
 * low, out, and payment failed. Collapsing them into one "billing problem"
 * banner leaves the merchant guessing which one they have.
 */
function QuotaNotice({ data }: { data: BillingOverview }) {
  if (data.status === "past_due") {
    return (
      <div role="alert" className="rounded-xl border border-warn/30 bg-warn/10 p-4 text-sm">
        <strong>We could not take your last payment.</strong> Your assistant is
        still answering — update your card to keep it that way.
      </div>
    );
  }

  if (data.exhausted) {
    // Which meter ran out changes what is still true, not just the number.
    // A spent message allowance stops every answer; a spent conversation
    // allowance only stops new conversations. Telling a merchant that
    // existing conversations continue when they have in fact stopped is the
    // kind of wrong that gets reported as an outage.
    const messagesOut = data.blocked_by === "messages";

    return (
      <div role="alert" className="rounded-xl border border-bad/30 bg-bad-soft p-4 text-sm text-bad">
        <strong>
          {messagesOut
            ? "Your assistant has stopped answering."
            : "Your assistant has stopped answering new questions."}
        </strong>{" "}
        {messagesOut ? (
          <>
            You have used all {data.messages_limit.toLocaleString()} messages in
            this period. Upgrade to start answering again.
          </>
        ) : (
          <>
            You have used all {data.conversations_limit.toLocaleString()}{" "}
            conversations in this period. Conversations already in progress
            continue; upgrade to start new ones again.
          </>
        )}
      </div>
    );
  }

  if (data.nearly_exhausted) {
    // Lead with whichever allowance will actually run out first.
    const messagesTighter = data.messages_fraction_used >= data.fraction_used;
    const left = messagesTighter
      ? data.messages_limit - data.messages_used
      : data.conversations_limit - data.conversations_used;

    return (
      <div role="alert" className="rounded-xl border border-warn/30 bg-warn/10 p-4 text-sm">
        <strong>
          {left.toLocaleString()} {messagesTighter ? "messages" : "conversations"}{" "}
          left this period.
        </strong>{" "}
        {messagesTighter
          ? "Your assistant stops answering when they run out."
          : "Your assistant stops answering new questions when they run out."}
      </div>
    );
  }

  if (data.cancel_at_period_end) {
    return (
      <div role="status" className="rounded-xl border border-line bg-surface p-4 text-sm">
        Your plan ends on {new Date(data.period_end).toLocaleDateString()}. After
        that you move to the free plan.
      </div>
    );
  }

  return null;
}

/**
 * Tone for one meter, from its own numbers.
 *
 * Deliberately not derived from `exhausted` / `nearly_exhausted`, which are
 * true when *either* meter is in trouble.
 */
function meterTone(used: number, limit: number): "bad" | "warn" | "default" {
  if (limit < 0) return "default";
  if (limit === 0 || used >= limit) return "bad";
  return used / limit >= 0.8 ? "warn" : "default";
}

/**
 * One bar per metered allowance.
 *
 * Both are shown because they run out independently and at different rates —
 * a store with long conversations exhausts messages first, a store with many
 * short ones exhausts conversations first — and a single bar cannot say which
 * is about to stop the assistant.
 */
function UsageBars({ data }: { data: BillingOverview }) {
  return (
    <>
      {data.conversations_limit >= 0 && (
        <MeterBar
          label="Conversations"
          used={data.conversations_used}
          limit={data.conversations_limit}
        />
      )}

      {data.messages_limit >= 0 && (
        <div className={data.conversations_limit >= 0 ? "mt-4" : undefined}>
          <MeterBar
            label="Messages"
            used={data.messages_used}
            limit={data.messages_limit}
            hint="One per answer the assistant gives"
          />
        </div>
      )}

      {data.plan.sync_limit >= 0 && (
        <div className="mt-4">
          <MeterBar
            label="Syncs"
            used={data.syncs_used}
            limit={data.plan.sync_limit}
            hint="Each time you press Sync now"
          />
        </div>
      )}

      {/* Every plan stops at its limit — there is no overage — so say so
          where the limits are, rather than leaving it to be discovered. */}
      <p className="mt-4 text-sm text-muted">
        When a limit is reached the assistant pauses until the next period, and we
        email you. Upgrading restores it straight away.
      </p>
    </>
  );
}

function MeterBar({
  label,
  used,
  limit,
  hint,
}: {
  label: string;
  used: number;
  limit: number;
  hint?: string;
}) {
  const percent = limit > 0 ? Math.min(100, Math.round((used / limit) * 100)) : 100;

  return (
    <div>
      <div className="mb-1 flex items-baseline justify-between gap-2">
        <span className="text-sm font-medium">{label}</span>
        {hint != null && <span className="text-xs text-muted">{hint}</span>}
      </div>

      {/* A real progress element: the percentage is announced rather than
          only drawn, and it degrades to a number without any styling. */}
      <progress
        value={used}
        max={limit}
        aria-label={`${label} used this period`}
        className="h-2 w-full"
      />
      <div className="mt-2 flex justify-between text-sm">
        <span className="tabular-nums">
          {used.toLocaleString()} of {limit.toLocaleString()} used ({percent}%)
        </span>
        <span className="text-muted tabular-nums">
          {Math.max(0, limit - used).toLocaleString()} left
        </span>
      </div>
    </div>
  );
}

function PlanCard({
  plan,
  current,
  busy,
  onChoose,
}: {
  plan: PlanSummary;
  current: boolean;
  busy: boolean;
  onChoose: () => void;
}) {
  return (
    <div
      className={`flex h-full flex-col rounded-xl border p-4 ${
        current ? "border-brand bg-brand-soft" : "border-line"
      }`}
    >
      <div className="flex items-center justify-between gap-2">
        <span className="font-medium">{plan.name}</span>
        {current && <Badge tone="brand">Current</Badge>}
      </div>

      <div className="mt-1 text-2xl font-semibold tabular-nums">
        {plan.price_cents === 0 ? "Free" : `$${(plan.price_cents / 100).toFixed(0)}`}
        {plan.price_cents > 0 && (
          <span className="text-sm font-normal text-muted"> /mo</span>
        )}
      </div>

      <ul className="mt-3 flex-1 space-y-1 text-sm text-muted">
        <li>
          {plan.conversation_limit < 0
            ? "Unlimited conversations"
            : `${plan.conversation_limit.toLocaleString()} conversations`}
        </li>
        <li>
          {plan.message_limit < 0
            ? "Unlimited messages"
            : `${plan.message_limit.toLocaleString()} messages`}
        </li>
        <li>{plan.site_limit < 0 ? "Unlimited sites" : `${plan.site_limit} sites`}</li>
        <li>
          {plan.product_limit < 0
            ? "Unlimited products"
            : `${plan.product_limit.toLocaleString()} products per store`}
        </li>
        <li>
          {plan.sync_limit < 0
            ? "Unlimited syncs"
            : `${plan.sync_limit.toLocaleString()} syncs a month`}
        </li>
      </ul>

      {!current && (
        <Button
          className="mt-3"
          variant={plan.price_cents > 0 ? "primary" : "secondary"}
          disabled={!plan.self_service || busy}
          onClick={onChoose}
        >
          {plan.trial_days > 0 ? `Start ${plan.trial_days}-day trial` : "Choose"}
        </Button>
      )}
    </div>
  );
}
